import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type {
  GetAllCostDatabaseItemsResponse,
  CostDatabaseItem,
  CostDatabaseData,
} from "@ServerTypes/costDatabase/costDatabase.schema";

const costDatabaseItemsSlice = createSlice({
  name: "costDatabaseItems",
  initialState: [] as CostDatabaseItem[],
  reducers: {
    getCostDatabaseItems: (_state: CostDatabaseItem[], action: PayloadAction<CostDatabaseItem[]>) => action.payload,
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      // The action type uniquely identifies `getCostDatabaseItemsData`'s fulfilled action, whose
      // payload is the endpoint body (actions/costDatabase.ts) — that is what fixes the payload type.
      (action): action is PayloadAction<GetAllCostDatabaseItemsResponse> =>
        action.type === "costDatabase/getCostDatabaseItemsData/fulfilled",
      (_state, action) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- SCHEMA DIVERGENCE: getAllCostDatabaseItemsResponse declares its rows INLINE with `_id`/`costDatabaseId`/`costDatabaseVersionId` as z.unknown(), while this slice is typed by costDatabaseItemSchema; the endpoint returns those same documents, so the fix is to build that response off costDatabaseItemSchema. Mapping the rows here instead is not an option — it would drop the extra projected members (id, measurementUnit, parentsList, …) the grid reads.
        (action.payload.result as CostDatabaseItem[] | undefined) ?? [],
    );
  },
});

const costDatabaseSlice = createSlice({
  name: "costDatabase",
  initialState: {} as CostDatabaseData,
  reducers: {
    setCostDatabase: (_state: CostDatabaseData, action: PayloadAction<CostDatabaseData | undefined>) =>
      action.payload ?? {},
  },
});

// `setCostDatabase` is intentionally exported raw (NOT wrapped with `Exact<>`).
// The consumer in actions/estimation.ts dispatches `response.data.result[0]` from
// a backend response that carries extra fields not declared on `CostDatabaseData`
// (e.g., `costDBversionId`, `versionNumber`, `currency`, `createdBy`, `createdDate`).
// Wrapping would break that valid call site.
export const { setCostDatabase } = costDatabaseSlice.actions;

export const getCostDatabaseItems = costDatabaseItemsSlice.reducer;
export const costDatabase = costDatabaseSlice.reducer;
